import React from 'react'
import {
  Card,
  CardContent,
  CardActions,
  Box,
  Typography,
  IconButton,
  useTheme,
  alpha,
} from '@mui/material'
import { MoreVert } from '@mui/icons-material'

interface ResponsiveCardProps {
  title?: string
  subtitle?: string
  children: React.ReactNode
  actions?: React.ReactNode
  icon?: React.ReactNode
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'
  hover?: boolean
  onClick?: () => void
  menu?: boolean
  onMenuClick?: (event: React.MouseEvent<HTMLElement>) => void
}

const ResponsiveCard: React.FC<ResponsiveCardProps> = ({
  title,
  subtitle,
  children,
  actions,
  icon,
  color = 'primary',
  hover = true,
  onClick,
  menu = false,
  onMenuClick,
}) => {
  const theme = useTheme()

  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: { xs: 2, sm: 3 },
        ...(hover && {
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[8],
            borderColor: alpha(theme.palette[color].main, 0.3),
          },
          // Mobile touch optimizations
          '@media (hover: none)': {
            '&:hover': {
              transform: 'none',
              boxShadow: theme.shadows[2],
            },
            '&:active': {
              transform: 'scale(0.98)',
              boxShadow: theme.shadows[4],
            },
          },
        }),
      }}
      onClick={onClick}
    >
      <CardContent sx={{ 
        pb: actions ? 1 : 2,
        p: { xs: 2, sm: 3 }, // Responsive padding
      }}>
        {(title || subtitle || icon || menu) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              mb: title || subtitle ? { xs: 1.5, sm: 2 } : 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, flex: 1 }}>
              {icon && (
                <Box
                  sx={{
                    p: { xs: 1, sm: 1.5 },
                    borderRadius: { xs: 1.5, sm: 2 },
                    bgcolor: alpha(theme.palette[color].main, 0.1),
                    color: `${color}.main`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: { xs: 36, sm: 44 },
                    minHeight: { xs: 36, sm: 44 },
                  }}
                >
                  {React.cloneElement(icon as React.ReactElement, {
                    sx: { fontSize: { xs: '1.2rem', sm: '1.5rem' } }
                  })}
                </Box>
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                {title && (
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: subtitle ? 0.5 : 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                    }}
                  >
                    {title}
                  </Typography>
                )}
                {subtitle && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Box>
            {menu && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  onMenuClick?.(e)
                }}
                sx={{ 
                  ml: 1,
                  p: { xs: 0.5, sm: 1 },
                  borderRadius: 1.5,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.action.hover, 0.8),
                  },
                  '&:active': {
                    transform: 'scale(0.9)',
                  },
                }}
              >
                <MoreVert sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }} />
              </IconButton>
            )}
          </Box>
        )}
        {children}
      </CardContent>
      {actions && (
        <CardActions sx={{ 
          pt: 0, 
          px: { xs: 2, sm: 3 }, 
          pb: { xs: 2, sm: 2 },
        }}>
          {actions}
        </CardActions>
      )}
    </Card>
  )
}

export default ResponsiveCard